/**
 * TickIcon (06 §4.3) — five outgoing delivery states with DISTINCT shapes, not
 * color alone (a11y §8): pending(clock) · sent(single check) · delivered(double
 * gray) · read(double blue) · failed(red bang). Custom SVG matches WhatsApp.
 */
import type { MessageStatus } from '../../data/model';
import styles from './TickIcon.module.css';

const LABEL: Record<MessageStatus, string> = {
  pending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed to send',
};

function SingleCheck() {
  return (
    <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
      <path
        d="M11.07.65 5.3 8.42 2.9 6.02a.6.6 0 0 0-.85.85l2.9 2.9c.24.24.64.2.83-.07L11.93 1.4a.6.6 0 1 0-.86-.75z"
        fill="currentColor"
      />
    </svg>
  );
}

function DoubleCheck() {
  return (
    <svg viewBox="0 0 18 11" width="18" height="11" aria-hidden="true">
      <path
        d="M14.07.65 8.3 8.42 6.6 6.7l-.78.95 2.05 2.05c.24.24.64.2.83-.07L14.93 1.4a.6.6 0 1 0-.86-.75zM10.07.65 4.3 8.42 1.9 6.02a.6.6 0 0 0-.85.85l2.9 2.9c.24.24.64.2.83-.07L10.93 1.4a.6.6 0 1 0-.86-.75z"
        fill="currentColor"
      />
    </svg>
  );
}

export function TickIcon({ status }: { status: MessageStatus }) {
  if (status === 'pending') {
    return (
      <span className={styles.tick} role="img" aria-label={LABEL.pending}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M8 4.5V8l2.3 1.4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className={`${styles.tick} ${styles.failed}`} role="img" aria-label={LABEL.failed}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="#fa3e3e" />
          <path d="M8 4v5M8 11v.01" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'sent') {
    return (
      <span className={`${styles.tick} ${styles.gray}`} role="img" aria-label={LABEL.sent}>
        <SingleCheck />
      </span>
    );
  }
  const read = status === 'read';
  return (
    <span
      className={`${styles.tick} ${read ? styles.read : styles.gray}`}
      role="img"
      aria-label={read ? LABEL.read : LABEL.delivered}
    >
      <DoubleCheck />
    </span>
  );
}
